// Ventes (E6) : bilan journalier par produit (volume et nombre de transactions agrégé) saisi par la station ou le
// distributeur, import CSV ou XLSX avec rapport ligne à ligne, anomalies de débit (variation supérieure à trois
// fois la moyenne mobile, paramètre), corrections motivées et tracées. Les ventes alimentent la consommation
// utilisée pour l'autonomie (D-027).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formaterNombre } from '@carburanttrack/shared';
import { api, messageErreur } from '../../api/client';
import { Message, MessageErreur, Squelette, Vide } from '../../composants/Etats';
import { date } from '../../composants/format';
import { AfficherPlus, quantite } from './communs';
import { nomProduit, useMesStations } from './Station';

interface LigneVente {
  id: string;
  installationId: string;
  station: string;
  produitCode: string;
  jour: string;
  volume: string;
  unite: string;
  nombreTransactions: number | null;
  etat: string;
  source: string;
  motifCorrection: string | null;
  droits: { corriger: boolean };
}

interface Anomalie {
  produitCode: string;
  jour: string;
  volume: string;
  moyenne: string;
  rapport: string;
}

interface RapportImport {
  lignesTotal: number;
  lignesAcceptees: number;
  lignesRejetees: number;
  rapport: { ligne: number; statut: string; motifs: string[] }[];
}

const hier = () => new Date(Date.now() + 3_600_000 - 86_400_000).toISOString().slice(0, 10);

export function PageVentes() {
  const client = useQueryClient();
  const { stations, chargement, erreur } = useMesStations();
  const [stationId, setStationId] = useState('');
  const courante = stations?.find((s) => s.id === stationId) ?? stations?.find((s) => s.gestion) ?? stations?.[0] ?? null;
  const produits = useQuery({ queryKey: ['station-produits', courante?.id], queryFn: async () => (await api<{ produitCode: string; unite: string }[]>(`/api/v1/stations/${courante!.id}/produits`)).donnees, enabled: Boolean(courante) });
  const ventes = useQuery({ queryKey: ['ventes', courante?.id], queryFn: async () => (await api<LigneVente[]>(`/api/v1/ventes?installationId=${courante!.id}&limite=120`)).donnees, enabled: Boolean(courante) });
  const anomalies = useQuery({ queryKey: ['ventes-anomalies', courante?.id], queryFn: async () => (await api<Anomalie[]>(`/api/v1/ventes/anomalies?installationId=${courante!.id}`)).donnees, enabled: Boolean(courante) });
  const [jour, setJour] = useState(hier);
  const [saisie, setSaisie] = useState<Record<string, { volume: string; transactions: string }>>({});
  const [correction, setCorrection] = useState<{ id: string; volume: string; motif: string } | null>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [nbVentes, setNbVentes] = useState(21);
  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: ['ventes'] });
    void client.invalidateQueries({ queryKey: ['ventes-anomalies'] });
  };
  const declarer = useMutation({
    mutationFn: async () =>
      api('/api/v1/ventes', {
        corps: {
          installationId: courante!.id,
          jour,
          lignes: Object.entries(saisie)
            .filter(([, v]) => v.volume.trim() !== '')
            .map(([produitCode, v]) => ({ produitCode, volume: v.volume.replace(/\s/g, '').replace(',', '.'), nombreTransactions: v.transactions.trim() ? Number(v.transactions) : null })),
        },
      }),
    onSuccess: () => {
      setSaisie({});
      rafraichir();
    },
  });
  const corriger = useMutation({
    mutationFn: async () => api(`/api/v1/ventes/${correction!.id}/correction`, { corps: { volume: correction!.volume.replace(/\s/g, '').replace(',', '.'), motif: correction!.motif } }),
    onSuccess: () => {
      setCorrection(null);
      rafraichir();
    },
  });
  const importer = useMutation({
    mutationFn: async () => {
      const f = new FormData();
      f.append('fichier', fichier!);
      return (await api<RapportImport>('/api/v1/ventes/import', { formulaire: f })).donnees;
    },
    onSuccess: () => rafraichir(),
  });
  if (chargement) return <Squelette lignes={5} />;
  if (erreur) return <MessageErreur erreur={erreur} titre="Stations indisponibles" />;
  if (!courante || !stations) return <Vide>Aucune station dont vous suivez les ventes.</Vide>;
  const auMoinsUn = Object.values(saisie).some((v) => v.volume.trim() !== '');
  return (
    <div>
      <div className="page-tete">
        <div>
          <span className="attenue">{courante.nom}</span>
          <h1>Ventes</h1>
          <p className="attenue">Bilan journalier par produit. Les ventes déclarées alimentent la consommation moyenne utilisée pour l’autonomie (D-027).</p>
        </div>
        {stations.length > 1 && (
          <div className="champ champ-etroit">
            <label htmlFor="v-station">Station</label>
            <select id="v-station" value={courante.id} onChange={(e) => setStationId(e.target.value)}>
              {stations.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {anomalies.data && anomalies.data.length > 0 && (
        <section className="carte-bloc" aria-labelledby="titre-anomalies-ventes">
          <h2 id="titre-anomalies-ventes">Anomalies de débit</h2>
          <ol className="liste-exceptions">
            {anomalies.data.map((a, i) => (
              <li key={`${a.produitCode}-${a.jour}`}>
                <span className="exception ambre">
                  <span className="numero" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="texte">
                    <span className="marque-exception">Variation de {formaterNombre(a.rapport, 1)} fois la moyenne</span>
                    <b>
                      {nomProduit(a.produitCode)}, {date(a.jour)} : {quantite(a.volume, 'L')}
                    </b>
                    <small>Moyenne mobile {quantite(a.moyenne, 'L')} par jour. Vérifiez la saisie ou corrigez-la avec un motif.</small>
                  </span>
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}
      <div className="grille-2">
        <form
          className="carte-bloc"
          aria-labelledby="titre-bilan"
          onSubmit={(e) => {
            e.preventDefault();
            declarer.mutate();
          }}
        >
          <h2 id="titre-bilan">Bilan du jour</h2>
          <div className="champ">
            <label htmlFor="v-jour">Jour</label>
            <input id="v-jour" type="date" value={jour} max={new Date(Date.now() + 3_600_000).toISOString().slice(0, 10)} onChange={(e) => setJour(e.target.value)} required />
          </div>
          {produits.isLoading && <Squelette lignes={2} />}
          {produits.data?.map((p) => (
            <div className="formulaire-ligne serre" key={p.produitCode}>
              <div className="champ">
                <label htmlFor={`v-${p.produitCode}`}>
                  {nomProduit(p.produitCode)} ({p.unite})
                </label>
                <input id={`v-${p.produitCode}`} inputMode="decimal" value={saisie[p.produitCode]?.volume ?? ''} onChange={(e) => setSaisie({ ...saisie, [p.produitCode]: { transactions: saisie[p.produitCode]?.transactions ?? '', volume: e.target.value } })} />
              </div>
              <div className="champ">
                <label htmlFor={`t-${p.produitCode}`}>Transactions</label>
                <input id={`t-${p.produitCode}`} inputMode="numeric" value={saisie[p.produitCode]?.transactions ?? ''} onChange={(e) => setSaisie({ ...saisie, [p.produitCode]: { volume: saisie[p.produitCode]?.volume ?? '', transactions: e.target.value } })} />
              </div>
            </div>
          ))}
          {declarer.error && <Message type="erreur">{messageErreur(declarer.error)}</Message>}
          {declarer.isSuccess && <Message type="succes">Bilan enregistré.</Message>}
          <button type="submit" className="bouton primaire" disabled={declarer.isPending || !auMoinsUn}>
            Enregistrer le bilan
          </button>
        </form>
        <form
          className="carte-bloc"
          aria-labelledby="titre-import-ventes"
          onSubmit={(e) => {
            e.preventDefault();
            importer.mutate();
          }}
        >
          <h2 id="titre-import-ventes">Import d’un fichier</h2>
          <p className="attenue">
            Fichier CSV ou XLSX, une ligne par station, jour et produit. <a href="/api/v1/ventes/modele.csv">Télécharger le modèle</a>
          </p>
          <div className="champ">
            <label htmlFor="v-fichier">Fichier</label>
            <input id="v-fichier" type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} required />
          </div>
          <button type="submit" className="bouton" disabled={importer.isPending || !fichier}>
            Importer
          </button>
          {importer.error && <Message type="erreur">{messageErreur(importer.error)}</Message>}
          {importer.data && (
            <Message type={importer.data.lignesRejetees ? 'avertissement' : 'succes'}>
              {importer.data.lignesAcceptees} ligne{importer.data.lignesAcceptees > 1 ? 's' : ''} acceptée{importer.data.lignesAcceptees > 1 ? 's' : ''} sur {importer.data.lignesTotal}.
              {importer.data.rapport
                .filter((r) => r.statut !== 'acceptee')
                .slice(0, 10)
                .map((r) => ` Ligne ${r.ligne} : ${r.motifs.join(', ')}.`)
                .join('')}
            </Message>
          )}
        </form>
      </div>
      <section className="carte-bloc espace-haut" aria-labelledby="titre-historique-ventes">
        <h2 id="titre-historique-ventes">Bilans déclarés</h2>
        {ventes.isLoading && <Squelette lignes={4} />}
        {ventes.error && <MessageErreur erreur={ventes.error} />}
        {ventes.data && ventes.data.length === 0 && <p className="attenue">Aucun bilan déclaré pour cette station.</p>}
        {correction && (
          <form
            className="formulaire-ligne"
            onSubmit={(e) => {
              e.preventDefault();
              corriger.mutate();
            }}
          >
            <div className="champ">
              <label htmlFor="vc-volume">Volume corrigé</label>
              <input id="vc-volume" inputMode="decimal" value={correction.volume} onChange={(e) => setCorrection({ ...correction, volume: e.target.value })} required />
            </div>
            <div className="champ">
              <label htmlFor="vc-motif">Motif de la correction</label>
              <input id="vc-motif" value={correction.motif} onChange={(e) => setCorrection({ ...correction, motif: e.target.value })} minLength={3} maxLength={500} required />
            </div>
            <div className="actions">
              <button type="submit" className="bouton primaire" disabled={corriger.isPending || correction.motif.trim().length < 3}>
                Enregistrer la correction
              </button>
              <button type="button" className="bouton" onClick={() => setCorrection(null)}>
                Annuler
              </button>
            </div>
            {corriger.error && <Message type="erreur">{messageErreur(corriger.error)}</Message>}
          </form>
        )}
        {ventes.data && ventes.data.length > 0 && (
          <div className="tableau-conteneur">
            <table className="tableau">
              <caption className="visuellement-cache">Bilans journaliers des ventes</caption>
              <thead>
                <tr>
                  <th scope="col">Jour</th>
                  <th scope="col">Produit</th>
                  <th scope="col" className="nombre">
                    Volume
                  </th>
                  <th scope="col" className="nombre masquable">
                    Transactions
                  </th>
                  <th scope="col" className="masquable">
                    Source
                  </th>
                  <th scope="col">
                    <span className="visuellement-cache">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ventes.data.slice(0, nbVentes).map((v) => (
                  <tr key={v.id}>
                    <td>{date(v.jour)}</td>
                    <td>
                      {nomProduit(v.produitCode)}
                      {v.etat === 'corrigee' && <small>Corrigée : {v.motifCorrection ?? 'motif non renseigné'}</small>}
                    </td>
                    <td className="nombre">{quantite(v.volume, v.unite)}</td>
                    <td className="nombre masquable">{v.nombreTransactions ?? <span className="cellule-indisponible">indisponible</span>}</td>
                    <td className="masquable">{v.source === 'import' ? 'import' : v.source === 'hors_ligne' ? 'hors connexion' : 'saisie'}</td>
                    <td>
                      {v.droits.corriger && (
                        <button type="button" className="bouton discret" onClick={() => setCorrection({ id: v.id, volume: v.volume, motif: '' })}>
                          Corriger
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {ventes.data && <AfficherPlus affiches={nbVentes} total={ventes.data.length} pas={42} libelle="bilans" onPlus={() => setNbVentes(nbVentes + 42)} />}
      </section>
    </div>
  );
}
